import React from 'react';
import { FiSearch, FiPlus, FiFileText } from 'react-icons/fi';
import { Flow, FlowType } from '../../../../../types/flow';
import { Input } from '../ui/FlowInput';
import { Button } from '../ui/FlowButton';
import { filterOptions } from '../editor/AdSlotFilters';

type AdslotSearchFilters = {
  adSlot?: string;
  make?: string;
  osVersions: string[];
  adTypes: string[];
  destTypes: string[];
  countries: string[];
};

interface FlowListProps {
  flows: Flow[];
  selectedFlowId: string | null;
  flowType: FlowType;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onSelectFlow: (flow: Flow) => void;
  onCreateNew: () => void;
  adslotSearchFilters: AdslotSearchFilters;
  onAdslotSearchFiltersChange: (next: AdslotSearchFilters) => void;
}

const typeLabelMap: Record<FlowType, string> = {
  pre: 'Pre Steps',
  post: 'Post Steps',
  country: 'Country',
  vpn: 'VPN',
  base: 'Base',
  adslot: 'AdSlot',
};

export function FlowList({
  flows,
  selectedFlowId,
  flowType,
  searchQuery,
  onSearchChange,
  onSelectFlow,
  onCreateNew,
  adslotSearchFilters,
  onAdslotSearchFiltersChange,
}: FlowListProps) {
  const filteredFlows = flows.filter(flow => {
    // name text search
    const matchesText = flow.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());

    if (!matchesText) return false;

    // only apply these extra checks for adslot
    if (flowType !== 'adslot') return true;

    const f = flow.filters;
    // if you apply adslot search filters, and flow has no filters, it can't match
    const anyFilterSelected =
      !!adslotSearchFilters.adSlot ||
      !!adslotSearchFilters.make ||
      adslotSearchFilters.osVersions.length > 0 ||
      adslotSearchFilters.adTypes.length > 0 ||
      adslotSearchFilters.destTypes.length > 0 ||
      adslotSearchFilters.countries.length > 0;

    if (anyFilterSelected && !f) return false;

    // single-select: must be included
    if (
      adslotSearchFilters.adSlot &&
      !f?.adSlots?.includes(adslotSearchFilters.adSlot)
    ) {
      return false;
    }
    if (
      adslotSearchFilters.make &&
      !f?.makes?.includes(adslotSearchFilters.make)
    ) {
      return false;
    }

    // multi-select: require ALL selected values to be present
    const requiresAll = (selected: string[], actual?: string[]) =>
      selected.every(v => (actual ?? []).includes(v));

    if (!requiresAll(adslotSearchFilters.osVersions, f?.osVersions))
      return false;
    if (!requiresAll(adslotSearchFilters.adTypes, f?.adTypes)) return false;
    if (!requiresAll(adslotSearchFilters.destTypes, f?.destTypes)) return false;
    if (!requiresAll(adslotSearchFilters.countries, f?.countries)) return false;

    return true;
  });

  const isTypeEmpty = flows.length === 0;

  function SingleSelect({
    label,
    options,
    value,
    onChange,
  }: {
    label: string;
    options: string[];
    value?: string;
    onChange: (v?: string) => void;
  }) {
    return (
      <div className="adslot-flow-search-filter">
        <div className="adslot-flow-search-filter-label">{label}</div>
        <select
          className="adslot-flow-search-filter-select"
          value={value ?? ''}
          onChange={e => onChange(e.target.value ? e.target.value : undefined)}
        >
          <option value="">Any</option>
          {options.map(opt => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
    );
  }

  function MultiChipSelect({
    label,
    options,
    selected,
    onChange,
  }: {
    label: string;
    options: string[];
    selected: string[];
    onChange: (v: string[]) => void;
  }) {
    const toggle = (opt: string) => {
      if (selected.includes(opt)) onChange(selected.filter(s => s !== opt));
      else onChange([...selected, opt]);
    };

    return (
      <div className="adslot-flow-search-filter">
        <div className="adslot-flow-search-filter-label">{label}</div>
        <div className="adslot-flow-search-filter-chips">
          {options.map(opt => {
            const active = selected.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onMouseDown={e => e.preventDefault()}
                onClick={() => toggle(opt)}
                className={
                  'adslot-chip adslot-chip-muted adslot-flow-search-filter-chip' +
                  (active ? ' adslot-flow-search-filter-chip--active' : '')
                }
              >
                {opt}
              </button>
            );
          })}
        </div>
        {selected.length > 0 && (
          <button
            type="button"
            className="adslot-flow-search-filter-clear"
            onMouseDown={e => e.preventDefault()}
            onClick={() => onChange([])}
          >
            Clear
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="adslot-flow-list-root">
      <div className="adslot-flow-list-header">
        {flowType === 'adslot' && (
          <div className="adslot-flow-search-filters-panel">
            <div className="adslot-flow-search-filters-title">
              Search Filters
            </div>

            <SingleSelect
              label="AdSlot"
              options={[...filterOptions.adSlots]}
              value={adslotSearchFilters.adSlot}
              onChange={adSlot =>
                onAdslotSearchFiltersChange({ ...adslotSearchFilters, adSlot })
              }
            />

            <SingleSelect
              label="Make"
              options={[...filterOptions.makes]}
              value={adslotSearchFilters.make}
              onChange={make =>
                onAdslotSearchFiltersChange({ ...adslotSearchFilters, make })
              }
            />

            <MultiChipSelect
              label="OS Versions"
              options={[...filterOptions.osVersions]}
              selected={adslotSearchFilters.osVersions}
              onChange={osVersions =>
                onAdslotSearchFiltersChange({
                  ...adslotSearchFilters,
                  osVersions,
                })
              }
            />

            <MultiChipSelect
              label="Ad Types"
              options={[...filterOptions.adTypes]}
              selected={adslotSearchFilters.adTypes}
              onChange={adTypes =>
                onAdslotSearchFiltersChange({ ...adslotSearchFilters, adTypes })
              }
            />

            <MultiChipSelect
              label="Destination Types"
              options={[...filterOptions.destTypes]}
              selected={adslotSearchFilters.destTypes}
              onChange={destTypes =>
                onAdslotSearchFiltersChange({
                  ...adslotSearchFilters,
                  destTypes,
                })
              }
            />

            <MultiChipSelect
              label="Countries"
              options={[...filterOptions.countries]}
              selected={adslotSearchFilters.countries}
              onChange={countries =>
                onAdslotSearchFiltersChange({
                  ...adslotSearchFilters,
                  countries,
                })
              }
            />
          </div>
        )}

        <div className="adslot-flow-list-search">
          <FiSearch className="adslot-icon-muted" />
          <Input
            placeholder="Search flows..."
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
          />
        </div>

        <Button onClick={onCreateNew} className="adslot-flow-list-new-btn">
          <FiPlus className="adslot-icon-inline" />
          New Flow
        </Button>
      </div>

      {/* Scrollable list */}
      <div className="adslot-flow-list-scroll">
        {filteredFlows.length === 0 ? (
          <div className="adslot-flow-list-empty">
            <div className="adslot-flow-list-empty-icon">
              <FiFileText />
            </div>
            {isTypeEmpty ? (
              <>
                <p className="adslot-flow-list-empty-title">
                  No flows yet for "{typeLabelMap[flowType]}"
                </p>
                <p className="adslot-flow-list-empty-sub">
                  Create your first {typeLabelMap[flowType].toLowerCase()} flow
                </p>
              </>
            ) : (
              <>
                <p className="adslot-flow-list-empty-title">
                  No flows match your search
                </p>
                <p className="adslot-flow-list-empty-sub">
                  Create new "{typeLabelMap[flowType].toLowerCase()}" flow
                </p>
              </>
            )}
            <Button size="sm" variant="outline" onClick={onCreateNew}>
              <FiPlus className="adslot-icon-inline" />
              Create Flow
            </Button>
          </div>
        ) : (
          filteredFlows.map(flow => (
            <button
              key={flow.id}
              type="button"
              onClick={() => onSelectFlow(flow)}
              className={
                'adslot-flow-list-item' +
                (selectedFlowId === flow.id
                  ? ' adslot-flow-list-item--active'
                  : '')
              }
            >
              <div className="adslot-flow-list-item-top">
                <span className="adslot-flow-list-item-name">{flow.name}</span>
                <span className={`adslot-chip adslot-chip-${flow.type}`}>
                  {typeLabelMap[flow.type]}
                </span>
              </div>

              {flow.filters && (
                <div className="adslot-flow-list-item-filters">
                  {(() => {
                    const MAX = 6;

                    const adSlots = flow.filters.adSlots ?? [];
                    const osVersions = flow.filters.osVersions ?? [];
                    const countries = flow.filters.countries ?? [];
                    const makes = flow.filters.makes ?? [];
                    const adTypes = flow.filters.adTypes ?? [];
                    const destTypes = flow.filters.destTypes ?? [];

                    // Buckets to keep related chips together
                    const groups = [
                      { key: 'adSlots', values: adSlots },
                      { key: 'osVersions', values: osVersions },
                      { key: 'countries', values: countries },
                      { key: 'makes', values: makes },
                      { key: 'adTypes', values: adTypes },
                      { key: 'destTypes', values: destTypes },
                    ] as const;

                    // Track which values we show per group
                    const shown: Record<string, string[]> = {};
                    groups.forEach(g => (shown[g.key] = []));

                    let used = 0;

                    // Step 1: baseline — first value of each group (in order)
                    for (const g of groups) {
                      if (used >= MAX) break;
                      if (g.values.length > 0) {
                        shown[g.key].push(g.values[0]);
                        used += 1;
                      }
                    }

                    // Step 2: fill remaining slots in priority order
                    const fillOrder = [
                      'osVersions',
                      'countries',
                      'adTypes',
                      'destTypes',
                    ] as const;

                    for (const key of fillOrder) {
                      if (used >= MAX) break;
                      const values = (flow.filters as any)[key] ?? [];
                      const already = shown[key];
                      for (
                        let i = already.length;
                        i < values.length && used < MAX;
                        i++
                      ) {
                        shown[key].push(values[i]);
                        used += 1;
                      }
                    }

                    // Step 3: render chips grouped, adding +N to the last chip of each group if needed
                    const chips: React.ReactNode[] = [];

                    for (const g of groups) {
                      const values = g.values;
                      const displayed = shown[g.key];
                      if (displayed.length === 0) continue;

                      displayed.forEach((val, idx) => {
                        chips.push(
                          <span
                            key={`${g.key}-${val}-${idx}`}
                            className="adslot-chip adslot-chip-muted"
                          >
                            {val}
                          </span>,
                        );
                      });

                      const hiddenCount = values.length - displayed.length;
                      if (hiddenCount > 0) {
                        const lastIndex = chips.length - 1;
                        const last = chips[lastIndex] as any;
                        chips[lastIndex] = (
                          <span
                            key={`${g.key}-more`}
                            className="adslot-chip adslot-chip-muted"
                          >
                            {last.props.children}+{hiddenCount}
                          </span>
                        );
                      }
                    }

                    return chips;
                  })()}
                </div>
              )}

              <div className="adslot-flow-list-item-tags">
                {flow.isDefault && (
                  <span className="adslot-chip adslot-chip-primary">
                    Default
                  </span>
                )}
                {flow.tags?.map(tag => (
                  <span key={tag} className="adslot-chip adslot-chip-muted">
                    {tag}
                  </span>
                ))}
              </div>

              <p className="adslot-flow-list-item-meta">
                Updated {flow.updatedAt} · by {flow.updatedBy}
              </p>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
